import React, { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import {
    ARMOR_SLOT_LABELS,
    getVipKitPlan,
    getVipKitIconSrc,
} from "@/data/vipKitConfig";
import { useI18n } from "@/i18n/I18nProvider";
import "./VipKitModal.css";

const INV_COLS = 6;
const MAIN_ROWS = 4;
const BELT_COLS = 6;
const WEAR_COLS = 7;
/** Slot da mochila (Rust: fica ao lado da roupa) — 8.ª coluna da fila de roupa. */
const WEAR_ROW_COLS = WEAR_COLS + 1;

function isRustKitPlan(plan) {
    return plan?.layout === "rust" || Array.isArray(plan?.main);
}

/**
 * @param {{ src: string | null; label?: string; qty?: number; accent?: string; compact?: boolean }} props
 */
function KitSlot({ src, label, qty, accent, compact }) {
    const { item, n } = useI18n();
    const size = compact
        ? "vip-kit-slot vip-kit-slot--compact"
        : "vip-kit-slot";
    return (
        <div
            className={size}
            title={label ? item(label) : undefined}
        >
            {src ? (
                <img src={src} alt="" className="vip-kit-slot__img" draggable={false} />
            ) : (
                <span className="vip-kit-slot__empty">—</span>
            )}
            {qty != null && qty > 1 ? (
                <span className="vip-kit-slot__qty">x{n(qty)}</span>
            ) : null}
            {accent ? <span className="vip-kit-slot__accent" style={{ background: accent }} /> : null}
        </div>
    );
}

function KitSectionTitle({ children }) {
    return <h3 className="vip-kit-section-title">{children}</h3>;
}

function SlotGrid({ cols, cells, tierId, accent, compact = true }) {
    return (
        <div
            className="vip-kit-grid"
            style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
            {cells.map((cell, i) => (
                <KitSlot
                    key={i}
                    src={cell?.file ? getVipKitIconSrc(tierId, cell.file) : null}
                    label={cell?.label}
                    qty={cell?.qty}
                    accent={accent}
                    compact={compact}
                />
            ))}
        </div>
    );
}

function ArmorRow({ slotLabel, slot, tierId, accent }) {
    const { item } = useI18n();
    const src = getVipKitIconSrc(tierId, slot?.file ?? null);
    return (
        <div className="vip-kit-armor-row">
            <div className="vip-kit-armor-row__text">
                <p className="vip-kit-armor-row__label">{slotLabel}</p>
                <p className="vip-kit-armor-row__name">{slot?.label ? item(slot.label) : "—"}</p>
            </div>
            <KitSlot src={src} label={slot?.label} qty={slot?.qty} accent={accent} compact />
        </div>
    );
}

function RustKitLayout({ plan, tierId, accent }) {
    const { t } = useI18n();
    const main = plan.main ?? Array(INV_COLS * MAIN_ROWS).fill(null);
    const belt = plan.belt ?? [];
    const wear = plan.wear ?? [];

    return (
        <div className="vip-kit-rust">
            <section>
                <KitSectionTitle>{t("vip.kitModal.main")}</KitSectionTitle>
                <SlotGrid cols={INV_COLS} cells={main} tierId={tierId} accent={accent} />
            </section>

            <section>
                <KitSectionTitle>{t("vip.kitModal.wear")}</KitSectionTitle>
                <SlotGrid
                    cols={WEAR_ROW_COLS}
                    cells={[...Array.from({ length: WEAR_COLS }, (_, i) => wear[i] ?? null), plan.backpack ?? null]}
                    tierId={tierId}
                    accent={accent}
                />
            </section>

            <section>
                <KitSectionTitle>{t("vip.kitModal.belt")}</KitSectionTitle>
                <SlotGrid
                    cols={BELT_COLS}
                    cells={Array.from({ length: BELT_COLS }, (_, i) => belt[i] ?? null)}
                    tierId={tierId}
                    accent={accent}
                />
            </section>
        </div>
    );
}

function LegacyKitLayout({ plan, tierId, accent }) {
    const { t } = useI18n();
    const hotbar = plan.hotbar ?? [];
    const inventory = plan.inventory ?? [];
    const armor = plan.armor ?? [];

    return (
        <div className="flex flex-col gap-6 sm:gap-7">
            <section>
                <KitSectionTitle>
                    {t("vip.kitModal.inventory")}
                    <span className="vip-kit-section-meta">
                        {t("vip.kitModal.itemCount", { count: inventory.filter(Boolean).length })}
                    </span>
                </KitSectionTitle>
                <SlotGrid cols={INV_COLS} cells={inventory} tierId={tierId} accent={accent} />
            </section>

            <section>
                <KitSectionTitle>{t("vip.kitModal.hotbar")}</KitSectionTitle>
                <SlotGrid
                    cols={BELT_COLS}
                    cells={Array.from({ length: BELT_COLS }, (_, i) => hotbar[i] ?? null)}
                    tierId={tierId}
                    accent={accent}
                />
            </section>

            <section>
                <KitSectionTitle>{t("vip.kitModal.armor")}</KitSectionTitle>
                <div className="vip-kit-armor-list">
                    {ARMOR_SLOT_LABELS.map((lab, i) => (
                        <ArmorRow
                            key={lab}
                            slotLabel={t(`vip.kitModal.slot.${lab}`)}
                            slot={armor[i] ?? null}
                            tierId={tierId}
                            accent={accent}
                        />
                    ))}
                </div>
            </section>
        </div>
    );
}

/**
 * @param {{ open: boolean; tier: { id: string; name: string | { pt: string; en: string }; accent?: string } | null; onClose: () => void }} props
 */
export default function VipKitModal({ open, tier, onClose }) {
    const { t, l } = useI18n();
    const handleKey = useCallback(
        (e) => {
            if (e.key === "Escape") onClose();
        },
        [onClose],
    );

    useEffect(() => {
        if (!open) return undefined;
        document.addEventListener("keydown", handleKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", handleKey);
            document.body.style.overflow = prev;
        };
    }, [open, handleKey]);

    if (!open || !tier) return null;

    const plan = getVipKitPlan(tier.id);
    const accent = tier.accent || "#FFB800";

    const content = !plan ? (
        <p className="text-center text-sm text-zrs-offwhite/65">
            {t("vip.kitModal.empty")}
        </p>
    ) : isRustKitPlan(plan) ? (
        <RustKitLayout plan={plan} tierId={tier.id} accent={accent} />
    ) : (
        <LegacyKitLayout plan={plan} tierId={tier.id} accent={accent} />
    );

    const node = (
        <div className="vip-kit-overlay" role="presentation">
            <button
                type="button"
                className="vip-kit-overlay__backdrop"
                aria-label={t("vip.kitModal.closeModal")}
                onClick={onClose}
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="vip-kit-modal-title"
                className="vip-kit-dialog"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="vip-kit-dialog__head">
                    <div className="min-w-0">
                        <p className="vip-kit-dialog__eyebrow">{t("vip.kitModal.eyebrow")}</p>
                        <h2 id="vip-kit-modal-title" className="vip-kit-dialog__title">
                            {t("vip.kitModal.title", { name: l(tier.name) })}
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="vip-kit-dialog__close"
                        aria-label={t("vip.kitModal.close")}
                    >
                        <X size={18} />
                    </button>
                </div>
                <div className="vip-kit-dialog__body">{content}</div>
            </div>
        </div>
    );

    return typeof document !== "undefined" ? createPortal(node, document.body) : null;
}
